import {
  createReadonlyObservable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';

import { isLiquidityProvider } from '#/unified-rfqs/entities/drfq/domain';

import { PublicCounterpartiesService, StoredCp } from '../counterparties';

export interface Status {
  readonly allLpsSelectedOnly: boolean;
  readonly allFavoritesSelectedOnly: boolean;
}

export interface CpsFilter {
  readonly status: ReadonlyObservable<Status>;
  readonly teardown: () => void;
  readonly selectDeskNames: (deskNames: readonly string[]) => void;
  readonly selectAllLpsOnly: () => void;
  readonly selectAllFavoritesOnly: () => void;
  readonly clear: () => void;
}

export function createCpsFilter(
  cpsService: PublicCounterpartiesService,
): CpsFilter {
  const listObservable = cpsService.store.list();

  const [statusObservable, setStatus] = createReadonlyObservable(
    'cps-filter-status',
    getStatus(),
  );

  listObservable.observe(handleUpdate);

  function teardown() {
    listObservable.unobserve(handleUpdate);
  }

  function getStatus(): Status {
    const allCps = listObservable.get();
    return calculateStatus(allCps);
  }

  function handleUpdate() {
    setStatus(getStatus());
  }

  function setSelectedWithPredicate(predicate: (cp: StoredCp) => boolean) {
    // Using same timestamp for keeping correct ordering
    const now = Date.now();

    for (const cp of listObservable.get()) {
      const shouldBeSelected = predicate(cp);
      const selected = cp.selectedAt !== 0;

      if (shouldBeSelected) {
        // We must select even if already selected, to set the same
        // selectedAt timestamp and guarantee correct ordering
        cpsService.setSelected(cp.id, true, now);
      } else if (selected) {
        cpsService.setSelected(cp.id, false);
      }
    }
  }

  function selectDeskNames(deskNames: readonly string[]) {
    setSelectedWithPredicate(storedCpDeskNames(deskNames));
  }

  function selectAllLpsOnly() {
    setSelectedWithPredicate(storedCpIsLp);
  }

  function selectAllFavoritesOnly() {
    setSelectedWithPredicate(storedCpIsFavorite);
  }

  function clear() {
    setSelectedWithPredicate(() => false);
  }

  return {
    status: statusObservable,
    teardown,
    selectDeskNames,
    selectAllLpsOnly,
    selectAllFavoritesOnly,
    clear,
  };
}

function calculateStatus(allCps: readonly StoredCp[]): Status {
  let allFavoritesSelectedOnly = true;
  let allLpsSelectedOnly = true;
  let hasFavorite = false;
  let hasLp = false;

  for (const cp of allCps) {
    const selected = cp.selectedAt !== 0;

    const { isFavorite } = cp;
    if (isFavorite) {
      hasFavorite = true;
    }
    if (allFavoritesSelectedOnly && (isFavorite ? !selected : selected)) {
      allFavoritesSelectedOnly = false;
    }

    const isLp = isLiquidityProvider(cp.data);
    if (isLp) {
      hasLp = true;
    }
    if (allLpsSelectedOnly && (isLp ? !selected : selected)) {
      allLpsSelectedOnly = false;
    }
  }

  // If 0 cps we can't mark as all of the 0 cps are selected 😅
  allFavoritesSelectedOnly &&= hasFavorite;
  allLpsSelectedOnly &&= hasLp;

  return { allLpsSelectedOnly, allFavoritesSelectedOnly };
}

function storedCpDeskNames(
  deskName: readonly string[],
): (cp: StoredCp) => boolean {
  return (cp) => deskName.includes(cp.data.desk_name);
}

function storedCpIsLp(storedCp: StoredCp): boolean {
  return isLiquidityProvider(storedCp.data);
}

function storedCpIsFavorite(storedCp: StoredCp): boolean {
  return storedCp.isFavorite;
}
